import {
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Pause,
  LucideIcon,
} from 'lucide-react';
import { OrganizationSubscriptionStatus } from '@models/subscription';

export interface SubscriptionStatusLabels {
  subscriptionStatusTrial: string;
  subscriptionStatusTrialExpired: string;
  subscriptionStatusActive: string;
  subscriptionStatusPastDue: string;
  subscriptionStatusCanceled: string;
  subscriptionStatusPaused: string;
}

export interface SubscriptionStatusConfig {
  icon: LucideIcon;
  text: string;
  className: string;
  badgeVariant: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
}

/**
 * Status to icon, label and colours. Shared by the owner billing page (pill) and the
 * super-admin Organizations page (Badge), so the two cannot disagree about a status.
 */
export const getSubscriptionStatusConfig = (
  status: OrganizationSubscriptionStatus | string,
  labels: SubscriptionStatusLabels,
): SubscriptionStatusConfig => {
  switch (status) {
    case OrganizationSubscriptionStatus.TRIAL:
      return {
        icon: Clock,
        text: labels.subscriptionStatusTrial,
        className:
          'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/20',
        badgeVariant: 'primary',
      };
    // Without this case the pill fell through to `default` and rendered the raw
    // enum — "TRIAL_EXPIRED" — in neutral grey, which read as a harmless label
    // rather than the blocking state it is.
    case OrganizationSubscriptionStatus.TRIAL_EXPIRED:
      return {
        icon: XCircle,
        text: labels.subscriptionStatusTrialExpired,
        className:
          'text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/20',
        badgeVariant: 'error',
      };
    case OrganizationSubscriptionStatus.ACTIVE:
      return {
        icon: CheckCircle,
        text: labels.subscriptionStatusActive,
        className:
          'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/20',
        badgeVariant: 'success',
      };
    case OrganizationSubscriptionStatus.PAST_DUE:
      return {
        icon: AlertCircle,
        text: labels.subscriptionStatusPastDue,
        className:
          'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/20',
        badgeVariant: 'warning',
      };
    case OrganizationSubscriptionStatus.CANCELED:
      return {
        icon: XCircle,
        text: labels.subscriptionStatusCanceled,
        className:
          'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/20',
        badgeVariant: 'error',
      };
    case OrganizationSubscriptionStatus.PAUSED:
      return {
        icon: Pause,
        text: labels.subscriptionStatusPaused,
        className:
          'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700',
        badgeVariant: 'secondary',
      };
    default:
      return {
        icon: AlertCircle,
        text: String(status),
        className:
          'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700',
        badgeVariant: 'secondary',
      };
  }
};
